import { createEmptyContentSnapshotV2 } from "../../runtime/content/ContentSnapshotV2";
import { DraftRuntimeContent } from "../runtime-preview/DraftRuntimeContent";
import type { ExpeditionState, Option } from "./expeditionRepository";
const ref = (rows: Option[], id: string | null) =>
  id
    ? (rows.find((row) => row.id === id) ?? {
        id,
        key: "missing",
        name: "Missing reference",
      })
    : null;
export class DraftExpeditionContentAdapter extends DraftRuntimeContent {
  load(state: ExpeditionState) {
    const snapshot = createEmptyContentSnapshotV2(
      `draft:${state.expeditions[0]?.updated_at ?? "empty"}`,
    );
    snapshot.assets = Object.fromEntries(
      state.assets.map((asset) => [asset.id, { ...asset, versionId: null }]),
    );
    snapshot.expeditions = Object.fromEntries(
      state.expeditions
        .filter((row) => !row.archived_at && row.final_reward_table_id && row.map_asset_id)
        .map((row) => [
          row.key,
          {
            id: row.id,
            key: row.key,
            name: row.name,
            summary: row.summary,
            difficulty: row.difficulty,
            recommendedPower: Number(row.recommended_power),
            partySize: 3 as const,
            troopsRequired: true as const,
            finalRewardTable: ref(state.rewards, row.final_reward_table_id)!,
            boardCardAssetId: row.board_card_asset_id,
            detailAssetId: row.detail_asset_id,
            mapAssetId: row.map_asset_id!,
            definitionVersion: row.definition_version,
            availability: row.availability,
            enabled: row.enabled,
            nodes: state.nodes
              .filter(
                (node) => node.expedition_id === row.id && !node.archived_at,
              )
              .map((node) => ({
                id: node.id,
                key: node.key,
                nodeKind: node.node_kind,
                title: node.title,
                bodyText: node.body_text,
                sceneAssetId: node.scene_asset_id,
                enemy: ref(state.enemies, node.enemy_id),
                rewardTable: ref(state.rewards, node.reward_table_id),
                failureModifier: ref(state.modifiers, node.failure_modifier_id),
                graphX: Number(node.map_x),
                graphY: Number(node.map_y),
                mapX: Number(node.map_x),
                mapY: Number(node.map_y),
                isFinal: node.is_final,
                readinessEffect: node.readiness_effect,
                readinessDelta: node.readiness_delta,
                repeatable: node.repeatable,
                sortOrder: node.sort_order,
                choices: state.choices
                  .filter(
                    (choice) =>
                      choice.node_id === node.id && !choice.archived_at,
                  )
                  .map((choice) => ({
                    id: choice.id,
                    label: choice.label,
                    checkType:
                      choice.check_type === "ATK"
                        ? ("army_attack" as const)
                        : choice.check_type === "DEF"
                          ? ("army_defence" as const)
                          : choice.check_type === "Power"
                            ? ("effective_power" as const)
                            : ("none" as const),
                    requirement: Number(choice.requirement),
                    successModifier: ref(
                      state.modifiers,
                      choice.success_modifier_id,
                    ),
                    failureModifier: ref(
                      state.modifiers,
                      choice.failure_modifier_id,
                    ),
                    successNodeId: state.edges.find((edge) => edge.choice_id === choice.id && edge.choice_outcome === "success" && !edge.archived_at)?.to_node_id ?? node.id,
                    failureNodeId: state.edges.find((edge) => edge.choice_id === choice.id && edge.choice_outcome === "failure" && !edge.archived_at)?.to_node_id ?? node.id,
                    successReadinessEffect: choice.success_readiness_effect,
                    successReadinessDelta: choice.success_readiness_delta,
                    failureReadinessEffect: choice.failure_readiness_effect,
                    failureReadinessDelta: choice.failure_readiness_delta,
                    successRewardTable: ref(
                      state.rewards,
                      choice.success_reward_table_id,
                    ),
                    failureRewardTable: ref(
                      state.rewards,
                      choice.failure_reward_table_id,
                    ),
                    sortOrder: choice.sort_order,
                  })),
              })),
            edges: state.edges
              .filter(
                (edge) => edge.expedition_id === row.id && !edge.archived_at,
              )
              .map((edge) => ({
                id: edge.id,
                fromNodeId: edge.from_node_id,
                toNodeId: edge.to_node_id,
                label: edge.label,
                conditionKey: edge.condition_key,
                sortOrder: edge.sort_order,
              })),
          },
        ]),
    );
    snapshot.expeditionBoard = {
      visibleSlots: state.config?.visible_slots ?? 2,
      refreshSeconds: state.config?.refresh_seconds ?? 3600,
      listings: state.listings
        .filter((row) => !row.archived_at)
        .map((row) => ({
          id: row.id,
          expeditionId: row.expedition_id,
          enabled: row.enabled,
          weight: row.weight,
          sortOrder: row.sort_order,
        })),
    };
    this.setSnapshot(snapshot);
    return this;
  }
}
